import {
   AREA_CODES,
   CAMPAIGNS,
   CONTENT_LIBRARY,
   demoResults,
   FILTER_VOCABULARY,
   NUMBER_POOL,
   NUMBER_PRICE_PER_MONTH,
   OVERVIEW,
   ORG,
   OWNED_NUMBERS,
   RECOMMENDED_BY_TYPE,
   RECOMMENDED_PLAYBOOK,
   SAMPLE_LEAD,
   STARTERS,
   TEAMMATES,
   VOICES,
} from '@forwardflow/features/campaign-flow/data/mock'
import {
   IAudience,
   IActivityItem,
   ICampaignStats,
   IFlowDraft,
   TPeriod,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { ICampaignFlowService } from '@forwardflow/features/campaign-flow/services/campaign-flow.service'
import {
   audienceTypes,
   interpretDescription,
} from '@forwardflow/features/campaign-flow/utils/describe'
import { localReadiness } from '@forwardflow/features/campaign-flow/utils/readiness'
import { withTypes } from '@forwardflow/features/campaign-flow/utils/playbook'
import { plural } from '@forwardflow/features/campaign-flow/utils/text'

const ago = (minutes: number) =>
   new Date(Date.now() - minutes * 60000).toISOString()

const DEMO_ACTIVITY: IActivityItem[] = [
   {
      id: 'a1',
      kind: 'transfer',
      leadId: '9001',
      leadName: 'Maya Brooks',
      campaignId: 'c-zillow-buyers',
      text: 'Sent live to your team',
      at: ago(7),
   },
   {
      id: 'a2',
      kind: 'appointment',
      leadId: '9002',
      leadName: 'Daniel Ortiz',
      campaignId: 'c-realtor-sellers',
      text: 'Booked an appointment for Thu 2:00 PM',
      at: ago(26),
   },
   {
      id: 'a3',
      kind: 'reply',
      leadId: '9003',
      leadName: 'Priya Shah',
      campaignId: 'c-zillow-buyers',
      text: 'Can you send me the 3 bed listings near Briargate?',
      at: ago(41),
   },
   {
      id: 'a5',
      kind: 'appointment',
      leadId: '9005',
      leadName: 'Ava Nguyen',
      campaignId: 'c-zillow-buyers',
      text: 'Booked an appointment for Fri 10:30 AM',
      at: ago(180),
   },
   {
      id: 'a6',
      kind: 'reply',
      leadId: '9006',
      leadName: 'Chris Hall',
      campaignId: 'c-realtor-sellers',
      text: 'We are thinking of selling in spring. What is ours worth?',
      at: ago(260),
   },
   {
      id: 'a7',
      kind: 'transfer',
      leadId: '9007',
      leadName: 'Lena Fischer',
      campaignId: 'c-cold-revive',
      text: 'Sent live to your team',
      at: ago(420),
   },
   {
      id: 'a8',
      kind: 'appointment',
      leadId: '9008',
      leadName: 'Marcus Reed',
      campaignId: 'c-cold-revive',
      text: 'Booked an appointment for Mon 9:00 AM',
      at: ago(1500),
   },
]

/** One campaign's page in the demo: its share of the results, and its setup. */
const demoCampaignStats = (id: string, period: TPeriod): ICampaignStats => {
   const row = CAMPAIGNS.find((c) => c.id === id)
   const share = row ? row.leads / 4574 : 0.1
   return {
      ...demoResults(period, share),
      name: row?.name ?? 'Your new campaign',
      kind: row?.kind ?? 'new-lead',
      status: row?.status ?? 'live',
      voiceName: row?.voiceName,
      voiceHue: row?.voiceHue,
      enrolled: row?.leads ?? 412,
      setup: {
         audience: row
            ? `${row.source} · ${row.id === 'c-cold-revive' ? 'Cold leads' : 'New leads'} · Buyers and sellers`
            : 'New leads · Buyers',
         timing: '5 touches over 3 days · 2 calls with voicemail, 2 texts',
         numbers: ['(719) 555-0140', '(303) 555-0158'],
         handoff: 'Team, in turns',
      },
   }
}

/** Front-end-only service/** Front-end-only service: fixed demo data, small delays, nothing leaves the browser. */
const wait = <T>(value: T, ms = 320): Promise<T> =>
   new Promise((resolve) => setTimeout(() => resolve(value), ms))
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const STORE_KEY = 'ff-campaign-flow-demo'
const readSaved = (): Record<string, IFlowDraft> => {
   try {
      return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}') as Record<
         string,
         IFlowDraft
      >
   } catch {
      return {}
   }
}
const writeSaved = (all: Record<string, IFlowDraft>) => {
   try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(all))
   } catch {
      /* private mode: the flow still works in memory */
   }
}

/** A stable, believable count from the filters, so changing a filter visibly changes the number. */
const KIND_BASE: Record<IAudience['kinds'][number], number> = {
   'new-lead': 1240,
   revive: 2170,
   activity: 430,
}
const TYPE_SHARE: Record<IAudience['leadTypes'][number], number> = {
   buyer: 0.62,
   seller: 0.28,
   rental: 0.1,
}

/** A stable, believable count from the filters, so every change visibly moves the number. */
export const mockLeadCount = (audience: IAudience): number => {
   const base = audience.kinds.reduce((s, k) => s + KIND_BASE[k], 0)
   /* No lead type picked = all leads. */
   const share = audienceTypes(audience).reduce((s, t) => s + TYPE_SHARE[t], 0)
   let n = Math.round(base * share)
   for (const f of audience.filters) {
      if (!f.value.length) continue
      n = Math.round(
         n *
            (f.operator === 'NOT_CONTAINS_ANY'
               ? 0.85
               : f.field === 'inquired_property'
                 ? 0.05 * f.value.length
                 : Math.min(0.95, 0.45 + (f.value.length - 1) * 0.15))
      )
   }
   return Math.max(n, audience.kinds.length ? 6 : 0)
}

export const campaignFlowMock: ICampaignFlowService = {
   campaigns: () => {
      /* Campaigns launched in this session come first, as a live row. */
      const launched = Object.values(readSaved())
         .filter((d) => d.status === 'active' && d.id)
         .map((d) => ({
            id: d.id as string,
            name: d.audience.name,
            source:
               d.audience.filters
                  .find(
                     (f) =>
                        f.field === 'source' &&
                        f.operator !== 'NOT_CONTAINS_ANY'
                  )
                  ?.value.map(String)
                  .join(', ') || 'All sources',
            status: 'live' as const,
            kind: d.audience.kinds[0] ?? null,
            voiceHue: VOICES.find((v) => v.id === d.voice.optionId)?.hue ?? 262,
            voiceName: d.voice.name,
            leads: d.audience.leadCount ?? 0,
            reached: 0,
            appointments: 0,
            transfers: 0,
            trend: Array(12).fill(0),
            lastActivity: 'Just launched',
         }))
      return wait([...launched, ...clone(CAMPAIGNS)])
   },
   overview: (period) => wait({ period, ...clone(OVERVIEW[period]) }),
   starters: () => wait(clone(STARTERS)),
   context: () =>
      wait(
         {
            org: clone(ORG),
            lead: clone(SAMPLE_LEAD),
            numberPrice: NUMBER_PRICE_PER_MONTH,
         },
         0
      ),

   interpretDescription: (text) => wait(interpretDescription(text), 700),
   estimateLeads: (audience) => wait(mockLeadCount(audience), 260),
   filterVocabulary: () => wait(clone(FILTER_VOCABULARY)),

   recommendedPlaybook: (audience) => {
      const pb = clone(RECOMMENDED_PLAYBOOK)
      const types = audienceTypes(audience)
      return wait(
         withTypes(
            {
               ...pb,
               byType: Object.fromEntries(
                  types.map((t) => [t, clone(RECOMMENDED_BY_TYPE[t])])
               ),
            },
            types
         )
      )
   },

   contentOptions: (type, slot) =>
      wait(
         CONTENT_LIBRARY.filter((c) => c.type === type && c.slot === slot).map(
            (c) => ({
               id: c.id,
               name: c.name,
               owner: c.owner,
               summary:
                  slot === 'script'
                     ? plural(c.content.script?.length ?? 0, 'point')
                     : `${plural(c.content.texts?.length ?? 0, 'text')} · ${plural(c.content.voicemails?.length ?? 0, 'voicemail')}`,
            })
         ),
         250
      ),
   loadContent: (_type, slot, id) => {
      const c = CONTENT_LIBRARY.find((x) => x.id === id)
      if (!c)
         return Promise.reject(
            new Error('That item is no longer in your library.')
         )
      return wait(
         clone(
            slot === 'script'
               ? {
                    script: c.content.script,
                    scriptName: c.name,
                    scriptId: c.id,
                 }
               : {
                    texts: c.content.texts,
                    voicemails: c.content.voicemails,
                    messageGroupName: c.name,
                    messageGroupId: c.id,
                 }
         ),
         250
      )
   },

   sendTestText: () => wait(undefined, 500),
   placeTestCall: () => wait(undefined, 500),

   voices: () => wait(clone(VOICES)),
   previewVoice: () => wait(null, 200),

   areaCodes: () => wait(clone(AREA_CODES)),
   availableNumbers: (search) => {
      const q = search.trim().toLowerCase().replace(/[()]/g, '')
      return wait(
         NUMBER_POOL.filter(
            (n) => n.areaCode.startsWith(q) || n.city.toLowerCase().includes(q)
         )
      )
   },
   ownedNumbers: () => wait(clone(OWNED_NUMBERS)),

   teammates: () => wait(clone(TEAMMATES)),

   saveDraft: (draft) => {
      const id = draft.id || `demo-${Date.now().toString(36)}`
      const all = readSaved()
      all[id] = {
         ...clone(draft),
         id,
         status: draft.status === 'new' ? 'draft' : draft.status,
      }
      writeSaved(all)
      return wait({ id }, 380)
   },

   review: (draft) => {
      const items = localReadiness(draft)
      const toBuy = draft.numbers.selected.filter((n) => !n.owned).length
      const costs = [
         {
            label: `${toBuy} new number${toBuy === 1 ? '' : 's'}`,
            amount: `$${toBuy * NUMBER_PRICE_PER_MONTH}/month`,
         },
         {
            label: 'Calls, texts and voicemails',
            amount: 'Included in your plan',
         },
      ]
      return wait({ ready: items.every((i) => i.ok), items, costs }, 420)
   },

   activate: (draft) => {
      if (draft.id) {
         const all = readSaved()
         all[draft.id] = { ...clone(draft), status: 'active' }
         writeSaved(all)
      }
      return wait(undefined, 600)
   },

   setPaused: () => wait(undefined, 200),

   campaignStats: (id, period) => wait(demoCampaignStats(id, period)),
   recentActivity: (campaignId) =>
      wait(
         clone(DEMO_ACTIVITY)
            .filter(
               (x) =>
                  !campaignId ||
                  x.campaignId === campaignId ||
                  !CAMPAIGNS.some((c) => c.id === campaignId)
            )
            .map((x) => ({
               ...x,
               campaignName: CAMPAIGNS.find((c) => c.id === x.campaignId)?.name,
            }))
      ),
}
